/*
 * Figma `48:30` — Text Field. State: Default, Focus, Error, Disabled.
 *
 * Focus is the browser's; error and disabled are the caller's. The error state is three things at
 * once and all three are required: `aria-invalid` so it is announced, a described-by pointing at
 * the message so the *reason* is announced with it, and a visible sentence with a glyph so the
 * state is not carried by a red border alone.
 *
 * `MaskedField` is the same control for a value that must not be shown in full. Tokens, provider
 * secrets and recovery credentials are masked by default and revealing one is a deliberate act — it
 * is never the initial render, and what it reveals is whatever the *server* chose to return, which
 * for most of these endpoints is already a suffix rather than the secret.
 */
import { forwardRef, useId, useState } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { IconWarning, IconEye, IconEyeOff } from './icons';
import { Button } from './Button';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** The message shown when the value is rejected. Its presence *is* the error state. */
  error?: string | null;
  hint?: string;
  /** Rendered inside the label. The control is still `required` for the browser's own validation. */
  requiredMark?: boolean;
  trailing?: ReactNode;
}

/*
 * The ref goes to the input itself, not the wrapper. A check-in desk has to keep focus in the code
 * box between scans — a keyboard scanner types into whatever has focus, so a desk that loses it
 * silently drops the next ticket.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, requiredMark, trailing, className = '', ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="jt-field">
      <label className="jt-field-label" htmlFor={id}>
        {label}
        {requiredMark && <span className="jt-field-required" aria-hidden="true">*</span>}
      </label>
      <div style={{ display: 'flex', gap: 'var(--jt-space-2)', alignItems: 'center' }}>
        <input
          {...rest}
          ref={ref}
          id={id}
          className={`jt-input ${className}`.trim()}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
        {trailing}
      </div>
      {hint && <div className="jt-field-hint" id={hintId}>{hint}</div>}
      {error && (
        <div className="jt-field-error" id={errorId}>
          <IconWarning size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
});

/**
 * A value the console holds but must not display in full.
 *
 * `masked` is what the screen shows until someone asks; `reveal` is called only when they do, and
 * may return nothing — an endpoint that will not hand back a secret is the correct answer, not an
 * error, and the control says so rather than appearing broken.
 */
export function MaskedValue({ masked, reveal, label }: {
  masked: string;
  /** Omit entirely where the full value is never retrievable. The reveal control is then not drawn. */
  reveal?: () => Promise<string | null> | string | null;
  label: string;
}) {
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--jt-space-2)' }}>
      <span className="jt-mono">{shown ?? masked}</span>
      {reveal && (
        <Button
          size="sm"
          variant="ghost"
          iconOnly
          icon={shown ? <IconEyeOff size={15} /> : <IconEye size={15} />}
          aria-label={shown ? `ซ่อน${label}` : `แสดง${label}`}
          disabled={busy}
          onClick={async () => {
            if (shown) { setShown(null); return; }
            setBusy(true); setFailed(false);
            try {
              const value = await reveal();
              if (value) setShown(value); else setFailed(true);
            } catch { setFailed(true); } finally { setBusy(false); }
          }}
        />
      )}
      {failed && <span className="jt-field-hint">ระบบไม่คืนค่าเต็มของ{label} — ออกใหม่แทนการดูของเดิม</span>}
    </span>
  );
}
